import { _notNil, _isEmptyString, _isNil } from '@/littledash';
import type { BaseSampleType, SampleTypeRow } from '@/model/SampleType.model';

export interface ValidationData {
  existing_titles: Array<string>;
  existing_labels: Array<string>;
  in_use_team_glossary_api_ids: Array<string>;
}

export interface ValidationResult {
  isValid: boolean;
  message?: string;
}

/**
 * Validates a sample type title for both create and edit scenarios
 */
export function validateSampleTypeValue(
  value: string,
  rowIndex: number,
  validationData: ValidationData | null,
  currentSampleTypes: Array<BaseSampleType> = [],
  spreadsheetData: Array<SampleTypeRow> = []
): ValidationResult {
  if (!_notNil(validationData)) {
    return { isValid: true, message: 'Loading validation data...' };
  }

  if (!_notNil(value) || typeof value !== 'string') {
    return { isValid: false, message: 'Sample type is required' };
  }

  const trimmedValue = value.trim();

  if (_isEmptyString(trimmedValue)) {
    return { isValid: false, message: 'Sample type is required' };
  }

  if (trimmedValue.length > 255) {
    return { isValid: false, message: 'Sample type must be 255 characters or less' };
  }

  const currentSampleType = currentSampleTypes[rowIndex];
  if (_notNil(currentSampleType)) {
    const isInUse = validationData.in_use_team_glossary_api_ids.includes(currentSampleType.api_id);
    if (isInUse && trimmedValue !== currentSampleType.title) {
      return { isValid: false, message: 'Cannot change title while samples reference this type' };
    }
  }

  const currentTitles = currentSampleTypes.map((st) => st.title);
  const existingTitles = validationData.existing_titles.filter((title) => !currentTitles.includes(title));

  const lowerTrimmedValue = trimmedValue.toLowerCase();

  if (spreadsheetData.length > 0) {
    const otherTitles = spreadsheetData
      .map((row, index) => ({ title: row[0]?.trim() || '', index }))
      .filter(({ title, index }) => index !== rowIndex && _notNil(title) && !_isEmptyString(title))
      .map(({ title }) => title);

    const lowerOtherTitles = otherTitles.map((title) => title.toLowerCase());

    if (lowerOtherTitles.includes(lowerTrimmedValue)) {
      return { isValid: false, message: 'Sample type must be unique within the spreadsheet' };
    }
  }

  const lowerExistingTitles = existingTitles.map((title) => title.toLowerCase());

  if (lowerExistingTitles.includes(lowerTrimmedValue)) {
    return { isValid: false, message: 'Sample type must be unique' };
  }

  return { isValid: true };
}

/**
 * Validates a sample type label for both create and edit scenarios
 */
export function validateSampleTypeLabel(
  value: string,
  rowIndex: number,
  validationData: ValidationData | null,
  currentSampleTypes: Array<BaseSampleType> = [],
  spreadsheetData: Array<SampleTypeRow> = []
): ValidationResult {
  if (!_notNil(validationData)) {
    return { isValid: true, message: 'Loading validation data...' };
  }

  if (!_notNil(value) || typeof value !== 'string') {
    return { isValid: true, message: 'Label is optional' };
  }

  const trimmedValue = value.trim();

  if (_isEmptyString(trimmedValue)) {
    return { isValid: true, message: 'Label is optional' };
  }

  if (trimmedValue.length > 10) {
    return { isValid: false, message: 'Label must be 10 characters or less' };
  }

  const currentLabels = currentSampleTypes.map((st) => st.label || '').filter((label) => label !== '');
  const existingLabels = validationData.existing_labels.filter((label) => !currentLabels.includes(label));

  const lowerTrimmedValue = trimmedValue.toLowerCase();

  if (spreadsheetData.length > 0) {
    const otherLabels = spreadsheetData
      .map((row, index) => ({ label: row[1]?.trim() || '', index }))
      .filter(({ label, index }) => index !== rowIndex && _notNil(label) && !_isEmptyString(label))
      .map(({ label }) => label);

    const lowerOtherLabels = otherLabels.map((label) => label.toLowerCase());

    if (lowerOtherLabels.includes(lowerTrimmedValue)) {
      return { isValid: false, message: 'Label must be unique within the spreadsheet' };
    }
  }

  const lowerExistingLabels = existingLabels.map((label) => label.toLowerCase());

  if (lowerExistingLabels.includes(lowerTrimmedValue)) {
    return { isValid: false, message: 'Label must be unique' };
  }

  return { isValid: true };
}

/**
 * Provides basic sample type validation error message for limited context scenarios
 * Used by validationErrorMessage functions that don't have access to full spreadsheet data
 */
export function getBasicSampleTypeErrorMessage(value: string, validationData: ValidationData | null): string {
  if (!_notNil(validationData)) {
    return 'Loading validation data...';
  }

  if (!_notNil(value) || typeof value !== 'string') {
    return 'Sample type is required';
  }

  const trimmedValue = value.trim();

  if (_isEmptyString(trimmedValue)) {
    return 'Sample type is required';
  }

  if (trimmedValue.length > 255) {
    return 'Sample type must be 255 characters or less';
  }

  const lowerTrimmedValue = trimmedValue.toLowerCase();
  const lowerExistingTitles = validationData.existing_titles.map((title) => title.toLowerCase());

  if (lowerExistingTitles.includes(lowerTrimmedValue)) {
    return 'Sample type must be unique';
  }

  return 'Sample type is invalid';
}

/**
 * Provides basic label validation error message for limited context scenarios
 * Used by validationErrorMessage functions that don't have access to full spreadsheet data
 */
export function getBasicLabelErrorMessage(value: string, validationData: ValidationData | null): string {
  if (!_notNil(validationData)) {
    return 'Loading validation data...';
  }

  if (!_notNil(value) || typeof value !== 'string') {
    return 'Label is optional';
  }

  const trimmedValue = value.trim();

  if (_isEmptyString(trimmedValue)) {
    return 'Label is optional';
  }

  if (trimmedValue.length > 10) {
    return 'Label must be 10 characters or less';
  }

  const lowerTrimmedValue = trimmedValue.toLowerCase();
  const lowerExistingLabels = validationData.existing_labels.map((label) => label.toLowerCase());

  if (lowerExistingLabels.includes(lowerTrimmedValue)) {
    return 'Label must be unique';
  }

  return 'Label is invalid';
}

/**
 * Validates that all selected detail options are valid.
 * Handles comma-separated strings with optional whitespace.
 */
export function validateDetailsOptions(value: unknown, availableOptions: Array<string>): boolean {
  if (_isNil(value) || value === '') {
    return true;
  }

  const selectedOptions =
    typeof value === 'string'
      ? value
          .split(',')
          .map((v) => v.trim())
          .filter(Boolean)
      : [];

  return selectedOptions.every((option) => availableOptions.includes(option));
}
